"use client"

import { Github, Twitter, Linkedin, Globe } from "lucide-react"
import Image from "next/image"
import Link from "next/link"

import { Button } from "@/core/ui/button"
import { blogPosts } from "@/data/blog"

interface Author {
    name: string
    image: string
    bio: string
    role?: string
}

interface AuthorCardProps {
    author: Author
}

// Get latest 2 blog posts for the sidebar
const latestPosts = blogPosts.slice(0, 2)

export function AuthorCard({ author }: AuthorCardProps) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left">
            <div className="relative w-24 h-24 shrink-0 rounded-full overflow-hidden border-2 border-white/10">
                <Image
                    src={author.image}
                    alt={author.name}
                    fill
                    sizes="96px"
                    className="object-cover"
                />
            </div>

            <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-1">{author.name}</h3>
                <p className="text-premium-cyan text-sm font-medium mb-3">{author.role || "Author"}</p>
                <p className="text-slate-400 text-sm leading-relaxed mb-4 max-w-lg mx-auto md:mx-0">
                    {author.bio}
                </p>

                <div className="flex items-center justify-center md:justify-start gap-2">
                    <Button asChild variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-400 hover:text-white hover:bg-white/10">
                        <a href="https://twitter.com/HaMi_Ayub" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                            <Twitter className="h-4 w-4" />
                        </a>
                    </Button>
                    <Button asChild variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-400 hover:text-white hover:bg-white/10">
                        <a href="https://www.linkedin.com/in/hamid-ayub/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                            <Linkedin className="h-4 w-4" />
                        </a>
                    </Button>
                    <Button asChild variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-400 hover:text-white hover:bg-white/10">
                        <a href="https://github.com/Hamid-Ayub" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                            <Github className="h-4 w-4" />
                        </a>
                    </Button>
                    <Button asChild variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-400 hover:text-white hover:bg-white/10">
                        <a href="https://hamidayub.com" aria-label="Website">
                            <Globe className="h-4 w-4" />
                        </a>
                    </Button>
                </div>
            </div>

            <div className="hidden md:flex flex-col gap-2 border-l border-white/10 pl-6 min-w-[140px]">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Latest</span>
                {latestPosts.map((post) => (
                    <Link
                        key={post.id}
                        href={post.slug}
                        className="text-sm text-slate-300 hover:text-premium-cyan truncate transition-colors"
                        title={post.title}
                    >
                        {post.title.length > 20 ? post.title.substring(0, 20) + "..." : post.title}
                    </Link>
                ))}
            </div>
        </div>
    )
}

